import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { IoHelpCircleOutline } from 'react-icons/io5';
import { authAPI, configAPI } from '../services/api';
import LanguageToggle, { getLang, setLang } from './LanguageToggle';
import HelpFeedbackModal from './HelpFeedbackModal';
import './Login.css';
import './HelpFeedbackModal.css';

// The Google OAuth Client ID is loaded by the backend from Secret Manager at
// startup (same machinery as JWT_SECRET / DB_PASSWORD) and exposed via
// /api/config/public. We fetch it once on mount — no frontend rebuild needed
// when the secret changes.
const GIS_SCRIPT_URL = 'https://accounts.google.com/gsi/client';
const DEFAULT_HOSTED_DOMAIN = 'telusmd.ca';

const S = {
  en: {
    subtitle:   'Sign in to your account',
    user_label: 'Username',
    user_ph:    'Enter your username',
    pass_label: 'Password',
    pass_ph:    'Enter your password',
    signing_in: 'Signing in…',
    sign_in:    'Sign In',
    or:         'Or continue with',
    telus_btn:  'Login with @telus Email',
    telusmd_btn:'Login with @telusmd Email',
    telusmd_signing_in: 'Connecting to Google…',
    telus_note: 'For TELUS Health Care Centres clinicians with a valid TELUS email address.',
    about_prism: 'About THCC PRISM',
    about_close: 'Close',
    help:       'Help',
    sso_unconfigured: 'Google sign-in is not configured yet. Please use your username and password.',
    sso_wrong_domain: 'Only @telusmd.ca accounts can sign in here.',
    sso_generic_err:  'Google sign-in failed. Please try again.',
    telus_unconfigured: 'TELUS sign-in is not configured yet. Please use your username and password.',
    telus_sso_err:      'TELUS sign-in failed or you do not have access to PRISM. Please try again or contact your administrator.',
  },
  fr: {
    subtitle:   'Connectez-vous à votre compte',
    user_label: "Nom d'utilisateur",
    user_ph:    "Entrez votre nom d'utilisateur",
    pass_label: 'Mot de passe',
    pass_ph:    'Entrez votre mot de passe',
    signing_in: 'Connexion…',
    sign_in:    'Connexion',
    or:         'Ou continuer avec',
    telus_btn:  'Connexion avec votre courriel @telus',
    telusmd_btn:'Connexion avec votre courriel @telusmd',
    telusmd_signing_in: 'Connexion à Google…',
    telus_note: 'Pour les cliniciens des Cliniques médicales TELUS Santé avec une adresse courriel TELUS valide.',
    about_prism: 'À propos de THCC PRISM',
    about_close: 'Fermer',
    help:       'Aide',
    sso_unconfigured: "La connexion Google n'est pas encore configurée. Veuillez utiliser votre nom d'utilisateur et votre mot de passe.",
    sso_wrong_domain: 'Seuls les comptes @telusmd.ca peuvent se connecter ici.',
    sso_generic_err:  'La connexion Google a échoué. Veuillez réessayer.',
    telus_unconfigured: "La connexion TELUS n'est pas encore configurée. Veuillez utiliser votre nom d'utilisateur et votre mot de passe.",
    telus_sso_err:      "La connexion TELUS a échoué ou vous n'avez pas accès à PRISM. Veuillez réessayer ou communiquer avec votre administrateur.",
  },
};

const ABOUT_PRISM_TEXT_EN = `About PRISM (Point-of-Care Real-time Intelligence System for Medicine)

Built Specifically for THCC Clinicians

PRISM integrates established **TELUS Health Care Centres** clinical protocols and decision logic — it understands our unique clinical environment. The co-pilot supports your Preventive Health Assessments, guides your clinical reasoning, accelerates research, and grounds continuing education, helping you practice efficiently with AI that works alongside your expertise.

Longevity & Preventive Focus

PRISM is grounded in **emerging and innovative science**, keeping you at the forefront of preventive medicine. It supports identifying risk trajectories early and enables true proactive intervention, complementing your clinical judgment, not replacing it. The co-pilot equips you to have deeper conversations with patients, partner on meaningful prevention strategies, and help them take ownership of their long-term health.

Canadian Data Sovereignty

Your data never leaves the country. We provide **100% Canadian data residency**, ensuring all data entered into PRISM is stored on secure Canadian servers.

Owned & Governed by TELUS Health

This isn't a third-party plugin. It is a **proprietary TELUS Health asset**. Because we own the stack, your usage directly improves our training content, making the tool smarter for your specific clinic's needs every single day. It utilizes a "closed-loop" environment where data is never used to train public models, adhering to the TELUS Trust Model.`;

const ABOUT_PRISM_TEXT_FR = `À propos de PRISM (Point-of-Care Real-time Intelligence System for Medicine)

Conçu spécifiquement pour les cliniciens des Cliniques TELUS Santé

PRISM intègre des protocoles cliniques et une logique décisionnelle établis des **Cliniques TELUS Santé**; il comprend notre environnement clinique unique. Ce copilote soutient vos évaluations en Santé préventive, guide votre raisonnement clinique, accélère la recherche et fonde la formation continue, vous aidant à exercer efficacement grâce à une IA qui travaille parallèlement à votre expertise.

Longévité et prévention

PRISM est fondé sur **une science émergente et innovante**, vous gardant à l'avant-garde de la médecine préventive. Il aide à identifier tôt les trajectoires de risque et permet une véritable intervention proactive, complétant votre jugement clinique sans le remplacer. Le copilote vous permet d'avoir des conversations plus approfondies avec les patients, de collaborer sur des stratégies de prévention significatives et de les aider à prendre en main leur santé à long terme.

Souveraineté des données canadiennes

Vos données ne quittent jamais le pays. Nous assurons une **résidence des données 100 % canadienne**, garantissant que toutes les données saisies dans PRISM sont stockées sur des serveurs canadiens sécurisés.

Propriété et gouvernance de TELUS Santé

Il ne s'agit pas d'un module d'extension tiers. C'est un **actif exclusif de TELUS Santé**. Comme nous possédons la technologie, votre utilisation améliore directement notre contenu de formation, rendant l'outil plus intelligent pour les besoins spécifiques de votre clinique au quotidien. Il utilise un environnement en « boucle fermée » où les données ne sont jamais utilisées pour entraîner des modèles publics, conformément au modèle de confiance de TELUS.`;

// Splits on **bold** markers so paragraph text can carry inline emphasis
// (headings are already bolded via the h3 render below).
function renderWithBold(text) {
  return text.split(/(\*\*[^*]+\*\*)/g).map((part, idx) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={idx}>{part.slice(2, -2)}</strong>;
    }
    return part;
  });
}

// Generate a fresh nonce for each GIS request. The same value is sent to
// Google (embedded in the issued ID token's `nonce` claim) and to the
// backend on /api/auth/google, where the two are compared.
function randomNonce() {
  const bytes = new Uint8Array(16);
  (window.crypto || window.msCrypto).getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

// Lazily inject the GIS script on first need so password-only sessions
// don't pay the cost.
let gisScriptPromise = null;
function loadGisScript() {
  if (typeof window === 'undefined') return Promise.reject(new Error('no window'));
  if (window.google?.accounts?.id) return Promise.resolve();
  if (gisScriptPromise) return gisScriptPromise;
  gisScriptPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${GIS_SCRIPT_URL}"]`);
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () => reject(new Error('GIS load failed')));
      return;
    }
    const script = document.createElement('script');
    script.src = GIS_SCRIPT_URL;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => {
      gisScriptPromise = null;
      reject(new Error('GIS load failed'));
    };
    document.head.appendChild(script);
  });
  return gisScriptPromise;
}

export default function Login({ onAuthenticated } = {}) {
  const [username,     setUsername]     = useState('');
  const [password,     setPassword]     = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error,        setError]        = useState('');
  const [loading,      setLoading]      = useState(false);
  const [ssoLoading,   setSsoLoading]   = useState(false);
  const [language,     setLanguage]     = useState(getLang);
  const [showAboutModal, setShowAboutModal] = useState(false);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const navigate = useNavigate();

  // The pending nonce is generated when the doctor clicks the SSO button and
  // verified by the backend. Kept in a ref so concurrent re-renders don't
  // race with the GIS callback.
  const nonceRef = useRef(null);
  // Runtime SSO config fetched from /api/config/public.
  const [googleClientId, setGoogleClientId] = useState('');
  const [googleHostedDomain, setGoogleHostedDomain] = useState(DEFAULT_HOSTED_DOMAIN);
  const [telusSsoEnabled, setTelusSsoEnabled] = useState(false);

  const s = S[language];

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const cfg = await configAPI.getPublicConfig();
        if (!mounted) return;
        setGoogleClientId(cfg?.google_client_id || '');
        if (cfg?.google_hosted_domain) setGoogleHostedDomain(cfg.google_hosted_domain);
        setTelusSsoEnabled(!!cfg?.telus_sso_enabled);
      } catch {
        // Backend unreachable or config endpoint failed — the SSO button
        // will surface "not configured" on click. Password login still works.
      }
    })();
    return () => { mounted = false; };
  }, []);

  // Surface the generic error when the TELUS callback bounces back to
  // /login?error=telus_sso (bad state, denied access, no PRISM group, etc.).
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('error') === 'telus_sso') {
      setError(S[getLang()].telus_sso_err);
      // Strip the query param so a reload doesn't re-show the error.
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  // Server-side OAuth2 redirect flow: a full-page navigation to the backend,
  // which 302s to TELUS PingID and 302s back to /chat on success. No GIS / JS
  // SDK involved (unlike the @telusmd Google flow above).
  const handleTelusClick = () => {
    setError('');
    if (!telusSsoEnabled) {
      setError(s.telus_unconfigured);
      return;
    }
    window.location.href = '/api/auth/telus/login';
  };

  const toggleLanguage = () => {
    const next = language === 'en' ? 'fr' : 'en';
    setLanguage(next);
    setLang(next);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await authAPI.login(username, password);
      if (typeof onAuthenticated === 'function') onAuthenticated();
      navigate('/chat');
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  // GIS callback fires after Google issues the ID token. We then POST it to
  // the PRISM backend for verification + session minting.
  const handleGoogleCredential = async (response) => {
    const credential = response?.credential;
    if (!credential) {
      setError(s.sso_generic_err);
      setSsoLoading(false);
      return;
    }
    try {
      await authAPI.loginWithGoogle(credential, nonceRef.current);
      nonceRef.current = null;
      if (typeof onAuthenticated === 'function') onAuthenticated();
      navigate('/chat');
    } catch (err) {
      const detail = err.response?.data?.detail || '';
      if (err.response?.status === 401) {
        setError(s.sso_wrong_domain);
      } else if (err.response?.status === 503) {
        setError(s.sso_unconfigured);
      } else {
        setError(detail || s.sso_generic_err);
      }
    } finally {
      setSsoLoading(false);
    }
  };

  // Re-init GIS on every click so the hosted_domain hint and nonce are fresh.
  // We drive GIS imperatively (instead of rendering Google's default button)
  // so the existing TELUS-purple styling stays in place.
  const handleTelusmdClick = async () => {
    setError('');
    if (!googleClientId) {
      setError(s.sso_unconfigured);
      return;
    }
    setSsoLoading(true);
    try {
      await loadGisScript();
    } catch {
      setError(s.sso_generic_err);
      setSsoLoading(false);
      return;
    }
    const nonce = randomNonce();
    nonceRef.current = nonce;
    try {
      window.google.accounts.id.initialize({
        client_id: googleClientId,
        hosted_domain: googleHostedDomain,
        hd: googleHostedDomain,
        callback: handleGoogleCredential,
        nonce,
        ux_mode: 'popup',
        auto_select: false,
        cancel_on_tap_outside: true,
      });
      window.google.accounts.id.prompt((notification) => {
        // If GIS can't show the One Tap prompt (cookies disabled, FedCM,
        // etc.), fall back to a popup picker so the doctor still gets a way
        // in. notification.isNotDisplayed/isSkippedMoment cover the cases
        // that need a fallback.
        const blocked =
          (notification.isNotDisplayed && notification.isNotDisplayed()) ||
          (notification.isSkippedMoment && notification.isSkippedMoment());
        if (blocked) {
          // The classic OAuth popup. Same hd enforcement; backend re-checks
          // the hd claim regardless of which UI path issued the token.
          const oauth = window.google.accounts.oauth2.initTokenClient
            ? window.google.accounts.oauth2.initTokenClient({
                client_id: googleClientId,
                scope: 'openid email profile',
                hosted_domain: googleHostedDomain,
                hd: googleHostedDomain,
                callback: () => {
                  // OAuth token client returns an access token, not an ID
                  // token — we can't use it. Surface a clear error rather
                  // than silently doing nothing.
                  setError(s.sso_generic_err);
                  setSsoLoading(false);
                },
              })
            : null;
          if (oauth) {
            oauth.requestAccessToken();
          } else {
            setError(s.sso_generic_err);
            setSsoLoading(false);
          }
        }
      });
    } catch {
      setError(s.sso_generic_err);
      setSsoLoading(false);
    }
  };

  // If the doctor reloads /login while a stale GIS prompt is open, clean up.
  useEffect(() => {
    return () => {
      if (window.google?.accounts?.id?.cancel) {
        try { window.google.accounts.id.cancel(); } catch { /* noop */ }
      }
    };
  }, []);

  return (
    <div className="login-page">

      {/* EN/FR toggle — top-left */}
      <div className="login-lang-bar">
        <LanguageToggle language={language} onToggle={toggleLanguage} />
      </div>

      {/* Help — top-right */}
      <div className="login-help-bar">
        <button
          type="button"
          className="login-help-btn"
          onClick={() => setShowHelpModal(true)}
          aria-label={s.help}
          title={s.help}
        >
          <IoHelpCircleOutline className="login-help-btn__icon" aria-hidden="true" />
          <span className="login-help-btn__label">{s.help}</span>
        </button>
      </div>

      {/* TELUS Health logo — top center */}
      <div className="login-logo-bar">
        <img
          src={language === 'fr' ? '/TELUS_Sante_Cliniques_FR_Vert_2021_Digital_RGB.svg' : '/TELUS_Health_CareCentres_EN_Vert_2021_Digital_RGB.svg'}
          alt={language === 'fr' ? 'TELUS Santé' : 'TELUS Health'}
          className="login-logo"
        />
      </div>

      {/* Left: sweet pea illustration, bottom-anchored */}
      <div className="login-left" aria-hidden="true">
        <img
          src="/SweetPea_Purple_SideviewStem_BH_LR_RGB_0.jpg"
          alt=""
          className="login-iris"
        />
      </div>

      {/* Right: login form */}
      <div className="login-right">
        <div className="login-card">

          <h1 className="login-title">THCC PRISM</h1>
          <p className="login-subtitle">{s.subtitle}</p>

          <form onSubmit={handleSubmit} className="login-form" noValidate>
            {error && <div className="login-error">{error}</div>}

            <div className="login-field">
              <label htmlFor="username">{s.user_label}</label>
              <input
                type="text"
                id="username"
                value={username}
                onChange={e => setUsername(e.target.value)}
                placeholder={s.user_ph}
                required
                autoFocus
                autoComplete="username"
              />
            </div>

            <div className="login-field">
              <label htmlFor="password">{s.pass_label}</label>
              <div className="pass-wrapper">
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder={s.pass_ph}
                  required
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  className="pass-toggle"
                  onClick={() => setShowPassword(v => !v)}
                  tabIndex={-1}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? '🙈' : '👁'}
                </button>
              </div>
            </div>

            <button type="submit" className="btn-sign-in" disabled={loading}>
              {loading ? s.signing_in : s.sign_in}
            </button>
          </form>

          <div className="login-divider"><span>{s.or}</span></div>

          <button
            type="button"
            className="btn-telus-email"
            onClick={handleTelusClick}
            disabled={!telusSsoEnabled || loading || ssoLoading}
          >
            {s.telus_btn}
          </button>

          <button
            type="button"
            className="btn-telus-email btn-telusmd-email"
            onClick={handleTelusmdClick}
            disabled={ssoLoading || loading}
          >
            {ssoLoading ? s.telusmd_signing_in : s.telusmd_btn}
          </button>

          <p className="login-note">{s.telus_note}</p>

          <button
            type="button"
            className="btn-about-prism"
            onClick={() => setShowAboutModal(true)}
          >
            {s.about_prism}
          </button>
        </div>
      </div>

      <HelpFeedbackModal
        open={showHelpModal}
        onClose={() => setShowHelpModal(false)}
        defaultEmail=""
        language={language}
      />

      {showAboutModal && (
        <div
          className="about-modal-overlay"
          onClick={() => setShowAboutModal(false)}
          role="dialog"
          aria-modal="true"
          aria-label={s.about_prism}
        >
          <div className="about-modal" onClick={(e) => e.stopPropagation()}>
            <div className="about-modal-header">
              <h2>{s.about_prism}</h2>
              <button
                type="button"
                className="about-modal-close"
                onClick={() => setShowAboutModal(false)}
                aria-label={s.about_close}
              >
                ✕
              </button>
            </div>
            <div className="about-modal-body">
              {(language === 'fr' ? ABOUT_PRISM_TEXT_FR : ABOUT_PRISM_TEXT_EN).split('\n\n').map((para, i) => {
                const isHeading = i > 0 && para.split('\n').length === 1 && !para.endsWith('.');
                if (isHeading) {
                  return <h3 key={i} className="about-section-heading">{para}</h3>;
                }
                return <p key={i} className="about-paragraph">{renderWithBold(para)}</p>;
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
